/**
 * Central media manifest.
 *
 * `alt` describes only what is visibly in the frame. `location` and `year` are
 * present ONLY where the organisation has published that detail; every image
 * without them is listed in CONTENT_CONFIRMATION.md so the client can supply
 * project, date, place and photographer credit. Nothing here is invented.
 *
 * `objectPosition` keeps the subject in frame when an image is cropped to a
 * fixed aspect ratio.
 */
export type MediaItem = {
  id: string;
  /**
   * "asset" is a file in client/src/assets/images with build-time derivatives;
   * "upload" is a file added through the media library, served at its own URL
   * with no variants. Absent means "asset", which is what every row seeded from
   * this manifest is.
   */
  kind?: "asset" | "upload";
  /** Set only for an upload. The renderer emits a plain <img> for these. */
  url?: string;
  width?: number;
  height?: number;
  /**
   * Path of the source file under client/src/assets/images, without the
   * extension. An id rather than an imported object: this manifest seeds
   * MongoDB and is edited in the admin panel, so it cannot hold a value that
   * only a bundler can produce. The client resolves it through the generated
   * image registry.
   */
  asset: string;
  alt: string;
  caption: string;
  project?: string;
  location?: string;
  year?: string;
  credit?: string;
  objectPosition?: string;
  /** True where the frame contains identifiable children, needs written consent before publication. */
  needsConsentCheck?: boolean;
  /** Only `granted` may publish a frame that needs a consent check. */
  consentStatus?: "not-required" | "pending" | "granted" | "refused";
  /**
   * Responsive versions of an uploaded photograph, made when it arrived: a
   * ladder of widths in AVIF, WebP and JPEG (PNG where it is transparent).
   * Build-time assets carry theirs in the generated registry instead.
   */
  variants?: { width: number; height?: number; format: string; url: string }[];
  /** A 16px blurred preview of an upload, painted behind it while it loads. */
  blurDataUrl?: string;
};

const item = (m: MediaItem) => m;

export const brand = {
  /** The original supplied lockup: blue emblem, white wordmark (built for dark grounds). */
  logo: "brand/hiscus-logo",
  /**
   * Light-background lockup. The supplied wordmark is white and disappears on
   * white or mist, so it is recoloured to the brand navy; the emblem is untouched.
   */
  logoDark: "brand/hiscus-logo-dark",
  /**
   * Reversed lockup for dark backgrounds, derived from the same source file:
   * the blue ink becomes white and the white line art becomes transparent, so
   * the emblem keeps its detail instead of flattening into a disc.
   */
  logoReverse: "brand/hiscus-logo-reverse",
  logoAlt:
    "Himshikhara Socio-Cultural Society logo: a circular emblem showing mountains above a stupa",
};

/**
 * The previous hero, retained so the media row and any existing reference to it
 * still resolve. Not used by any page; selectable in the media library.
 */
export const heroLegacy = item({
  id: "hero-womens-session",
  asset: "hero/womens-session-materials",
  alt: "Women and facilitators together in a bright room, holding the printed learning materials from a session",
  caption: "Participants and facilitators with session materials.",
  project: "Empowering with Dignity",
});

export const womenGirls = [
  item({
    id: "wg-gathering",
    asset: "programs/women-girls/community-gathering",
    alt: "A large group of women seated together on the ground under a tree during a community session in Nepal",
    caption: "Women gather for a community session, the setting where most HISCUS programmes begin.",
    objectPosition: "50% 45%",
  }),
  item({
    id: "wg-session-group",
    asset: "programs/women-girls/menstrual-health-session-group",
    alt: "Women and facilitators standing and seated together in a hall, holding printed booklets and learning materials",
    caption: "Participants and facilitators with session materials at an Empowering with Dignity workshop.",
    project: "Empowering with Dignity",
    objectPosition: "50% 40%",
  }),
  item({
    id: "wg-pad-making",
    asset: "programs/women-girls/reusable-pad-making-session",
    alt: "A volunteer cutting pink fabric into reusable pad shapes on the floor of a hall while participants work nearby",
    caption: "Cutting fabric for reusable sanitary pads during a making session.",
    project: "Empowering with Dignity",
  }),
  item({
    id: "wg-fabric-cutting",
    asset: "programs/women-girls/pad-making-fabric-cutting",
    alt: "Four people around a table cutting and pinning fabric pieces for reusable pads",
    caption: "Preparing pad templates and fabric before a community session.",
    project: "Empowering with Dignity",
  }),
  item({
    id: "wg-soap-bars",
    asset: "programs/women-girls/handmade-soap-bars",
    alt: "Two people wearing gloves holding freshly made bars of handmade soap",
    caption: "Finished soap from a vocational skills session.",
    project: "Livelihood skills",
  }),
  item({
    id: "wg-soap-prep",
    asset: "programs/women-girls/soap-making-preparation",
    alt: "A participant crouching on the floor, stirring soap mixture in a blue container with a wooden stick",
    caption: "Mixing soap base during a livelihood skills workshop.",
    project: "Livelihood skills",
    objectPosition: "50% 40%",
  }),
  item({
    id: "wg-vocational",
    asset: "programs/women-girls/vocational-skills-training",
    alt: "A participant at a table holding a wrapped handmade product in front of a vocational training banner",
    caption: "Packaging handmade products at a vocational training session.",
    project: "Livelihood skills",
  }),
  item({
    id: "wg-workplace",
    asset: "programs/women-girls/workplace-group-session",
    alt: "Women seated in a circle with a facilitator during a group session inside a community workspace",
    caption: "A group session held at participants' own workplace.",
    objectPosition: "50% 45%",
  }),
  item({
    id: "wg-wellbeing",
    asset: "programs/women-girls/psychosocial-wellbeing-activity",
    alt: "A participant colouring a printed wellbeing worksheet during a group session",
    caption: "A reflective exercise from a psychosocial wellbeing session.",
    project: "Psychosocial wellbeing",
  }),
];

export const childrenEducation = [
  item({
    id: "ce-nestling-home",
    asset: "programs/children-education/sarangkot-nestling-home",
    alt: "A hillside building with a red-tiled roof and a signboard, overlooking a valley at Sarangkot",
    caption: "The Sarangkot Nestling Home, completed in April 2018.",
    project: "Sarangkot Nestling Home",
    location: "Sarangkot",
    year: "2018",
    objectPosition: "50% 55%",
  }),
  item({
    id: "ce-school",
    asset: "programs/children-education/school-students-learning",
    alt: "Rows of school students in blue uniforms seated on the ground with open books and school bags",
    caption: "Students at a school supported through education programmes.",
    project: "Education support",
    needsConsentCheck: true,
    objectPosition: "50% 45%",
  }),
  item({
    id: "ce-library",
    asset: "programs/children-education/after-school-library-session",
    alt: "A facilitator seated at a low table with children in a room lined with bookshelves",
    caption: "An after-school learning session in a community library space.",
    project: "Before and after-school learning",
    needsConsentCheck: true,
  }),
];

export const reliefResilience = [
  item({
    id: "rr-health-post",
    asset: "programs/relief-resilience/health-post-supplies-handover",
    alt: "Women handing over a package of supplies across a desk inside a health post office",
    caption: "Handing over supplies to health post staff.",
    project: "Health post support",
  }),
  item({
    id: "rr-riverside",
    asset: "programs/relief-resilience/riverside-community-support",
    alt: "Women and children gathered beside a river under a branded shelter during a community support activity",
    caption: "A riverside community support activity.",
    needsConsentCheck: true,
    objectPosition: "50% 40%",
  }),
  item({
    id: "rr-infrastructure",
    asset: "programs/relief-resilience/community-infrastructure-site",
    alt: "Aerial view of a fenced hillside community site with polytunnels, a paved courtyard and a covered structure",
    caption: "A community site improved through construction and renovation work.",
    project: "Construction and renovation",
    objectPosition: "50% 55%",
  }),
  item({
    id: "rr-supplies",
    asset: "programs/relief-resilience/supplies-handover-volunteers",
    alt: "Volunteers and staff carrying and handing over cardboard boxes of supplies indoors",
    caption: "Volunteers and staff carrying cardboard boxes indoors.",
  }),
  /**
   * The lead image for Health, Relief & Community Resilience.
   *
   * The card used to carry the aerial view of a hillside community site, which
   * reads as a school or a farm rather than as relief work, and the pillar is
   * about health access, flood response and the infrastructure that follows.
   * This frame is a riverside assessment with a community member explaining the
   * situation to the team: it is unambiguously response work, it has open sky
   * where the heading sits, and nobody in it is a child.
   *
   * The riverside distribution photograph would have been the stronger frame,
   * and it is not used here: it contains identifiable children and its consent
   * check has not come back.
   */
  item({
    id: "rr-assessment",
    asset: "programs/relief-resilience/riverside-field-assessment",
    alt: "A woman standing beside a river bank speaking with a small group of volunteers, with city buildings behind them",
    caption: "A riverside assessment visit with community members, ahead of flood response work.",
    project: "Flood response",
    objectPosition: "50% 45%",
  }),
];

/**
 * The homepage hero photograph.
 *
 * It changed, and the reason is worth keeping.
 *
 * The previous hero was an aerial view of a fenced community site. It documented
 * a real outcome, but at homepage scale the people were tiny and the large upper
 * half read as empty hillside. The opening frame should show the community, not
 * make a visitor search for it.
 *
 * This one is HISCUS's own workplace group-session photograph. Three things
 * decide it.
 *
 * Resolution: this is a 2560px-wide landscape original, enough for a full-bleed
 * hero even where a portrait phone's required width is driven by its height.
 *
 * Composition: the active group sits through the centre and right of the frame,
 * while the fibre at left gives the headline a quieter ground. The photograph
 * shows work taking place rather than a building or an empty landscape.
 *
 * Consent: the media record is not marked as needing a consent check. Images
 * with an outstanding check remain excluded from this decision.
 *
 * Everything about it is editable in the admin panel: the image itself, the
 * focal point, the overlay strength, the eyebrow, the heading, the paragraph
 * and both buttons.
 *
 * It is an alias for the Women & Girls entry rather than a duplicate media row:
 * one photograph, one id and one place to maintain its description.
 */
export const hero = womenGirls[7];

export const cultureDialogue = [
  item({
    id: "cd-dialogue",
    asset: "programs/culture-environment-dialogue/dialogue-recording-session",
    alt: "Five people seated around a low table with microphones during a recorded discussion, an organisation banner behind them",
    caption: "A recorded discussion from the Insights & Dialogue series.",
    project: "Insights & Dialogue",
    objectPosition: "50% 45%",
  }),
  item({
    id: "cd-academic",
    asset: "programs/culture-environment-dialogue/academic-talk-welcome",
    alt: "A speaker greeted with flowers in front of a lecture screen and a row of international flags",
    caption: "Welcoming a speaker at an academic session.",
    project: "Academic roundtables",
  }),
  item({
    id: "cd-cultivation",
    asset: "programs/culture-environment-dialogue/community-field-cultivation",
    alt: "Four people bent over rows of vegetables, weeding and tending a terraced field",
    caption: "Tending a community vegetable plot during environment and composting work.",
    project: "Waste management and composting",
  }),
];

export const charityTrek = [
  item({
    id: "ct-welcome",
    asset: "charity-trek/group-welcome-garlands",
    alt: "A group of Nepali hosts and international visitors wearing marigold garlands outside a building",
    caption: "A visiting group welcomed at the start of a Charity Trek programme.",
    project: "Charity Trek",
    objectPosition: "50% 45%",
  }),
  item({
    id: "ct-scarf",
    asset: "charity-trek/volunteer-welcome-scarf",
    alt: "Two visitors being welcomed with a ceremonial scarf by a member of staff in an office",
    caption: "Orientation and welcome before programme placements begin.",
    project: "Charity Trek",
  }),
  item({
    id: "ct-trail",
    asset: "charity-trek/annapurna-sunrise-trail",
    alt: "Snow-covered Himalayan peaks lit orange by sunrise, seen above dark forested ridges",
    caption: "Sunrise over the Annapurna range, the trekking context for the programme.",
    project: "Charity Trek",
  }),
];

export const volunteering = [
  item({
    id: "vol-cohort",
    asset: "volunteer/volunteer-cohort-office",
    alt: "Five volunteers and staff members posing together in an office in front of an organisation banner and Nepali flags",
    caption: "A volunteer cohort at the HISCUS office.",
  }),
  item({
    id: "vol-group",
    asset: "volunteer/international-volunteer-group",
    alt: "A group of international volunteers standing together in an office beneath a row of national flags",
    caption: "International volunteers at the end of a placement.",
  }),
  item({
    id: "vol-recognition",
    asset: "volunteer/recognition-handover-office",
    alt: "Staff, volunteers and community members gathered for a certificate handover beside an organisation banner",
    caption: "A recognition and handover moment at the office.",
    needsConsentCheck: true,
  }),
  item({
    id: "vol-planning",
    asset: "volunteer/volunteer-programme-planning",
    alt: "A volunteer writing notes at a desk with a computer and printer in an office",
    caption: "Programme planning and documentation work.",
  }),
  item({
    id: "vol-stitching",
    asset: "volunteer/volunteer-stitching-session",
    alt: "A volunteer seated on the floor stitching fabric in front of an organisation banner",
    caption: "Stitching work during a making session.",
  }),
  item({
    id: "vol-banner",
    asset: "volunteer/volunteers-with-organisation-banner",
    alt: "Two volunteers standing either side of a HISCUS banner printed in English and Nepali",
    caption: "Volunteers at the HISCUS office.",
  }),
];

export const galleryExtras = [
  item({
    id: "gal-prep-table",
    asset: "gallery/pad-making-preparation-table",
    alt: "Two volunteers at a wooden table measuring and cutting pink fabric with paper templates",
    caption: "Measuring templates ahead of a reusable pad session.",
  }),
  item({
    id: "gal-workshop-floor",
    asset: "gallery/materials-workshop-floor",
    alt: "A group seated on the floor of a room sorting natural fibre material from a large sheet",
    caption: "Sorting materials during a workshop.",
  }),
  item({
    id: "gal-craft-table",
    asset: "gallery/craft-session-table",
    alt: "Three people working with fabric, thread and small tools spread across a glass table",
    caption: "A making session in progress.",
  }),
  item({
    id: "gal-prototypes",
    asset: "gallery/handmade-pad-prototypes",
    alt: "Four people holding up large handmade pad-shaped prototypes in a workshop room",
    caption: "Holding up oversized teaching models used in menstrual health sessions.",
  }),
  item({
    id: "gal-sports",
    asset: "gallery/community-sports-session",
    alt: "Two people playing football together on a small artificial-turf pitch",
    caption: "A recreational session between programme activities.",
  }),
  item({
    id: "gal-travel",
    asset: "gallery/travelling-to-field-site",
    alt: "Six volunteers and staff seated together inside a vehicle on the way to a field site",
    caption: "Travelling out to a field site.",
  }),
  item({
    id: "gal-meal",
    asset: "gallery/volunteer-shared-meal",
    alt: "A volunteer holding a plate of food during a break in a programme day",
    caption: "A shared meal during a programme day.",
    objectPosition: "50% 35%",
  }),
];

/** Homepage mosaic, six frames not used elsewhere on the homepage. */
export const galleryPreview = [
  galleryExtras[0],
  galleryExtras[3],
  volunteering[0],
  galleryExtras[1],
  cultureDialogue[2],
  galleryExtras[5],
];

/** Every documentary image on the site, for the /gallery route. */
export const allMedia: MediaItem[] = [
  heroLegacy,
  ...womenGirls,
  ...childrenEducation,
  ...reliefResilience,
  ...cultureDialogue,
  ...charityTrek,
  ...volunteering,
  ...galleryExtras,
];

export type GalleryCategory =
  | "All"
  | "Women & Girls"
  | "Children & Education"
  | "Health, Relief & Resilience"
  | "Culture, Environment & Dialogue"
  | "Charity Trek"
  | "Volunteering";

export const galleryGroups: { category: Exclude<GalleryCategory, "All">; items: MediaItem[] }[] = [
  {
    category: "Women & Girls",
    items: [...womenGirls.filter((media) => media.id !== hero.id), ...galleryExtras.slice(0, 4)],
  },
  { category: "Children & Education", items: childrenEducation },
  {
    category: "Health, Relief & Resilience",
    // rr-supplies came from a file named for composting. Keep it in the media
    // library for review, but do not publicly categorise it until HISCUS
    // confirms which programme it documents.
    items: reliefResilience.filter((media) => media.id !== "rr-supplies"),
  },
  { category: "Culture, Environment & Dialogue", items: cultureDialogue },
  { category: "Charity Trek", items: [...charityTrek, galleryExtras[5]] },
  { category: "Volunteering", items: [...volunteering, galleryExtras[4], galleryExtras[6]] },
];
